import PropTypes from "prop-types";
import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  Label,
  Input,
  Alert,
  Button,
  Spinner,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import withRouter from "components/Common/withRouter";
import { postChangePassword } from "helpers/backend-helper";
import { loginSuccess } from "store/auth/login/actions";

const passwordSchema = Yup.string()
  .min(8, "Password must be at least 8 characters")
  .matches(
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
    "Password must include uppercase, lowercase, and a number"
  );

const ChangePassword = (props) => {
  document.title = "Change Password - ColdBlock Technologies Inc.";

  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.Login || {});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const validation = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      currentPassword: Yup.string().required("Current password is required"),
      newPassword: passwordSchema.required("New password is required"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword")], "Passwords must match")
        .required("Please confirm your new password"),
    }),
    onSubmit: async (values) => {
      setError("");
      setSubmitting(true);
      try {
        const response = await postChangePassword({
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        });
        const updatedUser = response?.data?.user;
        if (updatedUser) {
          dispatch(loginSuccess(updatedUser));
        }
        const role = Number(updatedUser?.role ?? user?.role);
        if ([3, 4].includes(role)) {
          props.router.navigate("/home", { replace: true });
        } else {
          props.router.navigate("/dashboard", { replace: true });
        }
      } catch (err) {
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to update password";
        setError(msg);
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card>
              <CardBody className="p-4">
                <h4 className="mb-2">Update your password</h4>
                <p className="text-muted mb-4">
                  For security, you must choose a new password before continuing.
                </p>

                {error && (
                  <Alert color="danger" className="mb-3">
                    {error}
                  </Alert>
                )}

                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    validation.handleSubmit();
                  }}
                >
                  <div className="mb-3">
                    <Label>Current password</Label>
                    <InputGroup>
                      <Input
                        name="currentPassword"
                        type={showCurrent ? "text" : "password"}
                        value={validation.values.currentPassword}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={
                          validation.touched.currentPassword &&
                          !!validation.errors.currentPassword
                        }
                      />
                      <InputGroupText
                        style={{ cursor: "pointer" }}
                        onClick={() => setShowCurrent((v) => !v)}
                      >
                        <i className={`mdi mdi-eye${showCurrent ? "" : "-off"}`} />
                      </InputGroupText>
                    </InputGroup>
                    {validation.touched.currentPassword &&
                      validation.errors.currentPassword && (
                        <div className="text-danger small mt-1">
                          {validation.errors.currentPassword}
                        </div>
                      )}
                  </div>

                  <div className="mb-3">
                    <Label>New password</Label>
                    <InputGroup>
                      <Input
                        name="newPassword"
                        type={showNew ? "text" : "password"}
                        value={validation.values.newPassword}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={
                          validation.touched.newPassword &&
                          !!validation.errors.newPassword
                        }
                      />
                      <InputGroupText
                        style={{ cursor: "pointer" }}
                        onClick={() => setShowNew((v) => !v)}
                      >
                        <i className={`mdi mdi-eye${showNew ? "" : "-off"}`} />
                      </InputGroupText>
                    </InputGroup>
                    {validation.touched.newPassword && validation.errors.newPassword && (
                      <div className="text-danger small mt-1">
                        {validation.errors.newPassword}
                      </div>
                    )}
                  </div>

                  <div className="mb-4">
                    <Label>Confirm new password</Label>
                    <InputGroup>
                      <Input
                        name="confirmPassword"
                        type={showConfirm ? "text" : "password"}
                        value={validation.values.confirmPassword}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={
                          validation.touched.confirmPassword &&
                          !!validation.errors.confirmPassword
                        }
                      />
                      <InputGroupText
                        style={{ cursor: "pointer" }}
                        onClick={() => setShowConfirm((v) => !v)}
                      >
                        <i className={`mdi mdi-eye${showConfirm ? "" : "-off"}`} />
                      </InputGroupText>
                    </InputGroup>
                    {validation.touched.confirmPassword &&
                      validation.errors.confirmPassword && (
                        <div className="text-danger small mt-1">
                          {validation.errors.confirmPassword}
                        </div>
                      )}
                  </div>

                  <Button color="primary" type="submit" disabled={submitting} block>
                    {submitting ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Updating...
                      </>
                    ) : (
                      "Update password"
                    )}
                  </Button>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

ChangePassword.propTypes = {
  router: PropTypes.object,
};

export default withRouter(ChangePassword);
